/**
 * 拍照录分(spec-non-academic-grade-entry F-2 / F-4):选或拍登记表照片 → 后端读码、涂掉姓名、
 * 识别 → 草稿卡:每行左边是照片里这一行的裁剪条,右边是识别值;标黄的(空白、拿不准、超满分)
 * 置顶,老师逐行核对 / 改 → 「确认入库」。入库后 10 分钟内可一键撤销。
 * AI 只到草稿;确认按钮是老师本人的令牌直接调后端(spec §6.1)。
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { authHeaders } from "../../api/school";
import { cancelDraft, commitDraft, draftImageUrl, draftRowCropUrl, scanSheetPhotos, undoDraft, type DraftRow, type GradeDraft } from "../../api/gpa";

/** 带凭证取图再显示(接口要 Bearer,裸 <img src> 会 401)。 */
export function AuthImg({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoked = false; let obj: string | null = null;
    fetch(src, { headers: authHeaders() }).then(async (r) => {
      if (!r.ok) throw new Error(String(r.status));
      obj = URL.createObjectURL(await r.blob());
      if (!revoked) setUrl(obj);
    }).catch(() => { if (!revoked) setFailed(true); });
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [src]);
  if (failed) return <span className={`inline-block bg-slate-100 text-[10px] text-slate-400 ${className ?? ""}`}>—</span>;
  if (!url) return <span className={`inline-block animate-pulse bg-slate-100 ${className ?? ""}`} />;
  return <img src={url} alt={alt} className={className} />;
}

const NOTES = ["absent", "exempt", "sick"] as const;
const BAD = new Set(["over_max", "invalid_label", "unreadable"]);

function needsLook(r: DraftRow): boolean {
  return r.flags.some((f) => f !== "left_class");
}

export function ScanDraftModal({ onClose, onCommitted, initialDraft, inline }: { onClose: () => void; onCommitted?: (d: GradeDraft) => void; initialDraft?: GradeDraft | null; inline?: boolean }) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<"pick" | "uploading" | "review" | "committing" | "done">(initialDraft ? "review" : "pick");
  const [draft, setDraft] = useState<GradeDraft | null>(initialDraft ?? null);
  const [err, setErr] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, { value: string; label: string; note: string }>>({});
  const [label, setLabel] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [result, setResult] = useState<{ batch_id: string; saved: number; undo_until: string } | null>(null);
  const [undone, setUndone] = useState(false);
  const [showPhoto, setShowPhoto] = useState<number | null>(null);

  useEffect(() => {
    if (!draft) return;
    setEdits(Object.fromEntries(draft.rows.map((r) => [r.student_id, { value: r.value === null ? "" : String(r.value), label: r.grade_label ?? "", note: r.note ?? "" }])));
    setLabel(`${draft.item_label} · ${t("gpa.grades.scan.sheetLabel")}`);
  }, [draft, t]);

  const sorted = useMemo(() => draft ? [...draft.rows].sort((a, b) => Number(needsLook(b)) - Number(needsLook(a)) || a.seq - b.seq) : [], [draft]);
  const percent = draft?.scale === "percent";
  const stats = useMemo(() => {
    if (!draft) return { filled: 0, blank: 0, look: 0, bad: 0 };
    let filled = 0, blank = 0, look = 0, bad = 0;
    for (const r of draft.rows) {
      const e = edits[r.student_id];
      const has = percent ? (e?.value ?? "").trim() !== "" : (e?.label ?? "") !== "";
      if (has) filled++; else blank++;
      if (needsLook(r)) look++;
      if (has && percent && (Number(e!.value) < 0 || Number(e!.value) > draft.max_score || Number.isNaN(Number(e!.value)))) bad++;
    }
    return { filled, blank, look, bad };
  }, [draft, edits, percent]);

  async function upload() {
    setErr(null); setPhase("uploading");
    try { const d = await scanSheetPhotos(files); setDraft(d); setPhase("review"); }
    catch (e) { setErr((e as Error).message); setPhase("pick"); }
  }
  async function confirm() {
    if (!draft) return;
    setErr(null); setPhase("committing");
    try {
      const rows = draft.rows.map((r) => { const e = edits[r.student_id]!; return { student_id: r.student_id, value: percent && e.value.trim() !== "" ? Number(e.value) : null, grade_label: !percent && e.label ? e.label : null, note: e.note || null }; });
      const out = await commitDraft(draft.id, { rows, label: label.trim() || null, scored_on: date || null });
      setResult(out); setPhase("done"); onCommitted?.(draft);
    } catch (e) { setErr((e as Error).message); setPhase("review"); }
  }
  async function undo() {
    if (!draft) return;
    setErr(null);
    try { await undoDraft(draft.id); setUndone(true); onCommitted?.(draft); } catch (e) { setErr((e as Error).message); }
  }
  async function cancel() {
    if (draft && phase === "review") { try { await cancelDraft(draft.id); } catch { /* 取消失败不阻塞关闭;草稿 24 小时自动过期 */ } }
    onClose();
  }

  const body = (
    <>
      <div className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <div>
          <div className="text-sm font-semibold text-slate-800">{t("gpa.grades.scan.title")}</div>
          {draft && <p className="text-xs text-slate-500">{draft.class_name} · {draft.item_label} · {t(`gpa.subjects.scaleOf.${draft.scale}`)}{draft.images.length > 1 ? ` · ${t("gpa.grades.scan.pages", { n: draft.images.length })}` : ""}</p>}
        </div>
        {!inline && <button type="button" onClick={() => void cancel()} className="text-slate-400 hover:text-slate-600" aria-label={t("common.close")}>×</button>}
      </div>
      {err && <p className="mx-4 mt-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" role="alert">{err}</p>}

      {(phase === "pick" || phase === "uploading") && (
        <div className="px-4 py-4 text-sm">
          <p className="mb-3 text-slate-600">{t("gpa.grades.scan.pickHint")}</p>
          <label className="block cursor-pointer rounded-lg border-2 border-dashed border-sky-300 bg-sky-50/40 px-4 py-6 text-center text-sky-700 hover:bg-sky-50">
            <input type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="gpa-scan-input"
              onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])])} />
            📷 {t("gpa.grades.scan.pick")}
          </label>
          {files.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-slate-600">
              {files.map((f, i) => <li key={i} className="flex items-center justify-between rounded bg-slate-50 px-2 py-1"><span className="truncate">{f.name} · {(f.size / 1024 / 1024).toFixed(1)} MB</span><button type="button" className="text-rose-500" onClick={() => setFiles(files.filter((_, j) => j !== i))}>×</button></li>)}
            </ul>
          )}
          <div className="mt-4 flex items-center justify-end gap-2">
            <span className="mr-auto text-xs text-slate-400">{t("gpa.grades.scan.privacy")}</span>
            <button type="button" onClick={() => void cancel()} className="rounded border border-slate-300 px-3 py-1">{t("common.cancel")}</button>
            <button type="button" disabled={files.length === 0 || phase === "uploading"} onClick={() => void upload()} className="rounded bg-sky-500 px-3 py-1 font-medium text-white hover:bg-sky-600 disabled:opacity-50" data-testid="gpa-scan-upload">
              {phase === "uploading" ? t("gpa.grades.scan.uploading") : t("gpa.grades.scan.recognize", { n: files.length })}
            </button>
          </div>
        </div>
      )}

      {draft && (phase === "review" || phase === "committing") && (
        <>
          {draft.warnings.length > 0 && <ul className="mx-4 mt-2 space-y-1">{draft.warnings.map((w, i) => <li key={i} className="rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">{w}</li>)}</ul>}
          <div className="grid gap-2 border-b border-slate-100 px-4 py-3 text-xs sm:grid-cols-3">
            <label className="sm:col-span-2">{t("gpa.grades.manual.label")}<input value={label} onChange={(e) => setLabel(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
            <label>{t("gpa.grades.manual.date")}<input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded border border-slate-200 px-2 py-1" /></label>
          </div>
          <p className="px-4 pt-2 text-[11px] text-slate-500">{t("gpa.grades.scan.reviewHint")} {draft.images.map((im) => <button key={im.n} type="button" className="ml-1 text-sky-600 hover:underline" onClick={() => setShowPhoto(im.n)}>{t("gpa.grades.scan.viewPhoto", { n: im.page })}</button>)}</p>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2">
            <table className="w-full text-xs" data-testid="gpa-draft-rows">
              <thead className="text-slate-400"><tr><th className="w-8 py-1 text-left font-normal">#</th><th className="py-1 text-left font-normal">{t("gpa.grades.scan.cols.photo")}</th><th className="py-1 text-left font-normal">{t("gpa.grades.col.name")}</th><th className="py-1 text-left font-normal">{percent ? t("gpa.grades.scan.cols.score") : t("gpa.grades.scan.cols.level")}</th><th className="py-1 text-left font-normal">{t("gpa.grades.scan.cols.note")}</th><th className="py-1 text-left font-normal">{t("gpa.grades.scan.cols.check")}</th></tr></thead>
              <tbody>
                {sorted.map((r) => {
                  const e = edits[r.student_id] ?? { value: "", label: "", note: "" };
                  const look = needsLook(r);
                  const bad = percent && e.value.trim() !== "" && (Number.isNaN(Number(e.value)) || Number(e.value) < 0 || Number(e.value) > draft.max_score);
                  return (
                    <tr key={r.student_id} className={`border-t border-slate-100 ${look ? "bg-amber-50/70" : ""}`} data-flags={r.flags.join(" ")}>
                      <td className="py-1 pr-1 text-slate-500">{r.seq}</td>
                      <td className="py-1 pr-2"><AuthImg src={draftRowCropUrl(draft.id, r.seq)} alt={`row ${r.seq}`} className="h-8 max-w-[14rem] rounded border border-slate-200 object-cover" /></td>
                      <td className="whitespace-nowrap py-1 pr-2"><span className="font-medium text-slate-800">{r.name_zh}</span>{r.name_en && <span className="ml-1 text-slate-400">{r.name_en}</span>}{r.existing && r.existing.count > 0 && <div className="text-[10px] text-slate-400">{t("gpa.grades.scan.existing", { n: r.existing.count, v: r.existing.last_label ?? r.existing.avg_pct ?? "—" })}</div>}</td>
                      <td className="py-1 pr-2">
                        {percent
                          ? <input type="number" inputMode="decimal" aria-label={`${r.name_zh} ${t("gpa.grades.scan.cols.score")}`} value={e.value} onChange={(ev) => setEdits({ ...edits, [r.student_id]: { ...e, value: ev.target.value } })} className={`w-20 rounded border px-2 py-0.5 text-right ${bad ? "border-rose-400 bg-rose-50" : "border-slate-200"}`} placeholder={`/${draft.max_score}`} />
                          : <select aria-label={`${r.name_zh} ${t("gpa.grades.scan.cols.level")}`} value={e.label} onChange={(ev) => setEdits({ ...edits, [r.student_id]: { ...e, label: ev.target.value } })} className={`rounded border border-slate-200 px-2 py-0.5 ${e.label ? "" : "text-slate-400"}`}>
                              <option value="">{t("gpa.grades.scan.unset")}</option>{draft.levels.map((lv) => <option key={lv.label} value={lv.label}>{lv.label}</option>)}
                            </select>}
                      </td>
                      <td className="py-1 pr-2">
                        <select aria-label={`${r.name_zh} ${t("gpa.grades.scan.cols.note")}`} value={e.note} onChange={(ev) => setEdits({ ...edits, [r.student_id]: { ...e, note: ev.target.value } })} className={`rounded border border-slate-200 px-1 py-0.5 ${e.note ? "text-amber-700" : "text-slate-400"}`}>
                          <option value="">—</option>{NOTES.map((n) => <option key={n} value={n}>{t(`gpa.grades.scan.note.${n}`)}</option>)}
                        </select>
                      </td>
                      <td className="py-1 text-[11px]">
                        {r.flags.filter((f) => f !== "low_confidence" || !r.flags.includes("blank")).map((f) => <span key={f} className={`mr-1 rounded px-1.5 py-0.5 ${BAD.has(f) ? "bg-rose-100 text-rose-700" : f === "blank" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-800"}`}>{t(`gpa.grades.scan.flag.${f}`)}</span>)}
                        {!look && <span className="text-emerald-600">✓ {Math.round(r.confidence * 100)}%</span>}
                        {look && !r.flags.includes("blank") && <span className="text-slate-400">{Math.round(r.confidence * 100)}%</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 text-sm">
            <span className="mr-auto text-xs text-slate-500">{t("gpa.grades.scan.summary", stats)}</span>
            <button type="button" onClick={() => void cancel()} className="rounded border border-slate-300 px-3 py-1">{t("gpa.grades.scan.discard")}</button>
            <button type="button" disabled={phase === "committing" || stats.filled === 0 || stats.bad > 0} onClick={() => void confirm()} className="rounded bg-emerald-600 px-3 py-1 font-medium text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="gpa-draft-commit">
              {phase === "committing" ? t("common.saving") : t("gpa.grades.scan.confirm", { n: stats.filled })}
            </button>
          </div>
        </>
      )}

      {draft && phase === "done" && result && (
        <div className="px-4 py-5 text-sm">
          <p className={`text-base font-medium ${undone ? "text-slate-600" : "text-emerald-700"}`}>{undone ? t("gpa.grades.scan.undone") : t("gpa.grades.scan.done", { n: result.saved })}</p>
          {!undone && <p className="mt-1 text-xs text-slate-500">{t("gpa.grades.scan.undoHint")}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {!undone && <button type="button" onClick={() => void undo()} className="rounded border border-rose-300 px-3 py-1 text-rose-700 hover:bg-rose-50" data-testid="gpa-draft-undo">{t("gpa.grades.scan.undo")}</button>}
            <Link to={`/classes/${draft.class_id}/grades`} className="rounded bg-sky-500 px-3 py-1 font-medium text-white hover:bg-sky-600" onClick={onClose}>{t("gpa.grades.scan.openGrades")}</Link>
            {!inline && <button type="button" onClick={onClose} className="rounded border border-slate-300 px-3 py-1">{t("common.close")}</button>}
          </div>
        </div>
      )}

      {draft && showPhoto !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => setShowPhoto(null)}>
          <AuthImg src={draftImageUrl(draft.id, showPhoto)} alt="photo" className="max-h-full max-w-full rounded bg-white" />
        </div>
      )}
    </>
  );
  if (inline) return <div className="flex flex-col rounded-xl border border-slate-200 bg-white" data-testid="gpa-scan-modal">{body}</div>;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-2 sm:p-4" onClick={() => void cancel()}>
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="gpa-scan-modal">{body}</div>
    </div>
  );
}
